import { useEffect, useState } from "react";
import "react-lazy-load-image-component/src/effects/blur.css";
import { useDispatch } from "react-redux";
import "react-responsive-carousel/lib/styles/carousel.min.css"; // requires a loader
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { setToken } from "../../core/feature/token/tokenSlice";
import { setUser } from "../../core/feature/user/userSlice";
import { credentialCheck } from "../../core/service/api";
import { SavingIcon, SwitchIcon } from "./icons";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import {
  getAccountSaving,
  getSummarySaving,
  getMutationSaving,
  getDataSaving,
} from "../../core/service/api_saving";
import Company from "../../core/models/company";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Keyboard, Navigation, Pagination } from "swiper";
import { BalanceCard } from "./components/balanceCard";
import { SavingType } from "./savingTypes";

import "swiper/css";
import "swiper/css/pagination";
import { useTranslation } from "react-i18next";
import User from "../../core/models/user";
import { useAppSelector } from "../../core/feature/hooks";
const SavingMobile = (props: any) => {
  const dispatch = useDispatch();
  const { token } = useParams();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const user: User = useAppSelector((state) => state.user.user);

  const [searchParams] = useSearchParams();
  const callerIdFromUrl = searchParams.get("callerId");

  const [company, setCompany] = useState<Company | null>(null);

  const [activeCallerId, setActiveCallerId] = useState<string | null>(null);
  const [callerIdentities, setCallerIdentities] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [totalElements, setTotalElements] = useState<number>(0);

  const [activeUser, setActiveuser] = useState<any>(
    user.accounts[0].callerIdentities[0] || {},
  );

  const [summaryByCoa, setSummaryByCoa] = useState<any>(null);
  const [globalSummary, setGlobalSummary] = useState({
    totalBalance: 0,
    totalDepositAmount: 0,
    totalWithdrawalAmount: 0,
    totalTransaction: 0,
  });

  const [activeSaving, setActiveSaving] = useState<{
    coaCode: string;
    configId: string;
  } | null>(null);

  const [mutationSaving, setMutationSaving] = useState<Record<string, any[]>>(
    {},
  );

  const activeAccount = accounts.find(
    (acc) => acc.configId === activeSaving?.configId,
  );

  const [isAccOpen, setIsAccOpen] = useState(false);

  const [lastSaving, setLastSaving] = useState<
    Record<string, { coaCode: string; configId: string }>
  >({});

  const today = new Date();
  const startDate = new Date();
  startDate.setMonth(today.getMonth() - 1);
  const formatDate = (date: Date) => {
    const d = String(date.getDate()).padStart(2, "0");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = date.getFullYear();
    return `${d}-${m}-${y}`;
  };

  const mutationDateRange = `${formatDate(startDate)}/${formatDate(today)}`;

  useEffect(() => {
    const storedToken = localStorage.getItem("token_redux");
    if (token) {
      localStorage.setItem("token_redux", token);
      dispatch(setToken(token));
    } else if (!storedToken) {
      navigate("/");
      return;
    }
    credentialCheck()
      .then((res) => {
        dispatch(setUser(res.data.user));
        const identities = res.data.user?.accounts?.[0]?.callerIdentities ?? [];
        if (!identities.length) {
          navigate("/");
          return;
        }
        setCallerIdentities(identities);

        const validCaller =
          identities.find((i: any) => i.callerId === callerIdFromUrl)
            ?.callerId || identities[0].callerId;
        setActiveCallerId(validCaller);
      })
      .catch(() => navigate("/"));
  }, [token, callerIdFromUrl]);

  useEffect(() => {
    if (!activeCallerId) return;
    getDataSaving(activeCallerId).then((res: any) => {
      const list = res.data || [];
      setAccounts(list);
      if (list.length === 0) {
        setActiveSaving(null);
        return;
      }
      setActiveSaving({ coaCode: list[0].coaCode, configId: list[0].configId });
    });
  }, [activeCallerId]);

  useEffect(() => {
    if (!accounts.length || !activeCallerId) return;
    Promise.all(
      accounts.map((acc) =>
        getSummarySaving(activeCallerId, acc.coaCode, acc.configId).then(
          (res: any) => res.data,
        ),
      ),
    ).then((summaries) => {
      const global = summaries.reduce(
        (total: any, item: any, idx: any) => {
          total.totalDepositAmount += Number(item?.totalDepositAmount || 0);
          total.totalWithdrawalAmount += Number(
            item?.totalWithdrawalAmount || 0,
          );
          total.totalTransaction += Number(item?.totalTransaction || 0);
          total.totalBalance += Number(accounts[idx]?.balance || 0);
          return total;
        },
        {
          totalBalance: 0,
          totalDepositAmount: 0,
          totalWithdrawalAmount: 0,
          totalTransaction: 0,
        },
      );
      setGlobalSummary(global);
    });
  }, [accounts]);

  useEffect(() => {
    if (!activeCallerId || !activeSaving || !accounts.length) return;
    getSummarySaving(
      activeCallerId,
      activeSaving?.coaCode,
      activeSaving?.configId,
    ).then((res: any) => {
      setSummaryByCoa(res.data);
    });
    getMutationSaving(
      activeCallerId,
      activeSaving?.coaCode,
      activeSaving?.configId,
      mutationDateRange,
      "dateTime,Desc",
    ).then((res: any) => {
      setMutationSaving((prev) => ({
        ...prev,
        [activeSaving.configId]: res.data.content || [],
      }));
      console.log(mappedMutations(res.data.content));
      setPage(res.data.pageable.pageNumber);
      setTotalElements(res.data.totalElements);
      setMutationConfig(mappedMutations(res.data.content));
    });
  }, [
    activeCallerId,
    activeSaving?.coaCode,
    activeSaving?.configId,
    mutationDateRange,
  ]);

  const addMoreMutationSaving = () => {
    if (activeSaving) {
      getMutationSaving(
        activeCallerId,
        activeSaving?.coaCode,
        activeSaving?.configId,
        mutationDateRange,
        "dateTime,Desc",
        page + 1,
        10,
      ).then((res: any) => {
        setMutationSaving((prev) => ({
          ...prev,
          [activeSaving.configId]: res.data.content || [],
        }));
        setPage(res.data.pageable.pageNumber);
        setTotalElements(res.data.totalElements);
        setMutationConfig(
          mutationConfig.concat(mappedMutations(res.data.content)),
        );
      });
    }
  };

  useEffect(() => {
    setCompany(JSON.parse(localStorage.getItem("company") || ""));
    setGlobalSummary({
      totalBalance: 0,
      totalDepositAmount: 0,
      totalWithdrawalAmount: 0,
      totalTransaction: 0,
    });
  }, [activeCallerId]);

  const [mutationConfig, setMutationConfig] = useState<any>([]);

  // format styling
  const totalTabungan = accounts.length;
  const totalTransaksi = mutationConfig?.length;
  type FirstBoxesProps = {
    title: string;
    desc: string;
  };
  const FirstBoxes = ({ title, desc }: FirstBoxesProps) => (
    <div className="w-full bg-white rounded-xl py-3 px-3 flex flex-col border">
      <span className="text-[0.6em] text-wrap text-gray-500">{title}</span>

      <span className="text-xs font-bold">{desc}</span>
    </div>
  );

  type MutationStatus = "saving" | "cashout" | "reversed";
  const mutationType = {
    saving: {
      title: "Tabungan",
      icon: "bg-[#0054E6]",
      sign: "+ ",
    },
    cashout: {
      title: "Penarikan",
      icon: "bg-[#EA0C0C]",
      sign: "- ",
    },
    reversed: {
      title: "Reversal",
      icon: "bg-[#FFBC00]",
      sign: "+ ",
    },
  };
  type mappedType = {
    id: string;
    status: MutationStatus;
    date: string;
    amount: number;
  };

  const mappedMutations = (data: any) => {
    return data.map((item: any) => {
      let status: MutationStatus;
      let amount: number;
      if (item.status === "REVERSED") {
        status = "reversed";
        amount = Number(item.credit) > 0 ? item.credit : item.debit;
      } else if (Number(item.credit) > 0) {
        status = "saving";
        amount = item.credit;
      } else {
        status = "cashout";
        amount = item.debit;
      }
      return {
        id: item.id,
        status,
        date: new Date(item.dateTime).toLocaleString("id-ID", {
          day: "2-digit",
          month: "numeric",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
        amount: Number(amount),
      };
    });
  };
  // const mappedMutations = mutationConfig
  //   .filter((item) => Number(item.credit) > 0 || Number(item.debit) > 0)
  //   .sort(
  //     (a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime(),
  //   )

  const resetSaving = () => {
    setAccounts([]);
    setActiveSaving(null);
    setMutationSaving({});
    setSummaryByCoa(null);
    setGlobalSummary({
      totalBalance: 0,
      totalDepositAmount: 0,
      totalWithdrawalAmount: 0,
      totalTransaction: 0,
    });
  };

  return (
    <div className="h-screen overflow-y-scroll mb-6 bg-psp-primary top-to-bottom font-montserrat">
      {/* account section */}
      <section className="flex flex-col gap-3 mb-2 bg-psp-primary top-0 sticky z-50  px-4 pt-6">
        <div className="flex d-flex w-ful bg-psp-primaryl">
          <button>
            <ChevronLeft
              className="bg-white w-8 h-8 rounded-lg border p-1"
              onClick={() => navigate(`/home`)}
            />
          </button>
          <div className="absolute left-1/2 -translate-x-1/2 font-bold text-xl">
            Tabungan
          </div>
        </div>
        <div className="flex flex-row h-full py-3 bg-white w-full rounded-3xl border">
          <div className="flex flex-row justify-between w-full ml-2">
            <div className="flex flex-col px-3 justify-center font-montserrat">
              <h1 className={`text-sm mb-2 break-all font-semibold`}>
                {activeUser?.name ?? "-"}
              </h1>
              <h1 className="text-xs -mt-2">{activeUser?.callerId ?? "-"}</h1>
              <h1 className="text-xs -mt-2">{company?.name ?? "-"}</h1>
            </div>
            {callerIdentities.length > 1 && (
              <div className="flex items-center my-auto mr-3">
                <button
                  className="bg-psp-btn-primary p-2 rounded-3xl mt-0 text-white mr-4 flex d-flex justify-between items-center text-xs gap-3"
                  onClick={() => setIsAccOpen(true)}
                >
                  <SwitchIcon />
                </button>
              </div>
            )}
          </div>
        </div>
      </section>
      <div className="flex flex-col h-full  px-4 py-6">
        <div className="flex-none">
          {/* dashboard section */}
          <section className="">
            <Swiper
              spaceBetween={5}
              slidesPerView={2.1}
              loop={false}
              keyboard={{
                enabled: true,
              }}
              navigation={true}
              modules={[Autoplay, Pagination, Navigation, Keyboard]}
              className="w-full mb-10 sm:mb-20 p-2"
            >
              <SwiperSlide className="flex justify-center items-center">
                <FirstBoxes
                  title="Saldo Keseluruhan"
                  desc={`Rp. ${Number(
                    globalSummary.totalBalance ?? 0,
                  ).toLocaleString("id-ID")},00`}
                />
              </SwiperSlide>
              <SwiperSlide className="flex justify-center items-center">
                <FirstBoxes
                  title="Total Penarikan "
                  desc={`Rp. ${Number(
                    globalSummary?.totalWithdrawalAmount ?? 0,
                  ).toLocaleString("id-ID")},00`}
                />
              </SwiperSlide>
              <SwiperSlide className="flex justify-center items-center">
                <FirstBoxes
                  title="Total Saldo Masuk"
                  desc={`Rp. ${Number(
                    globalSummary?.totalDepositAmount ?? 0,
                  ).toLocaleString("id-ID")},00`}
                />
              </SwiperSlide>
              <SwiperSlide className="flex justify-center items-center">
                <FirstBoxes title="Total Tabungan" desc={`${totalTabungan}`} />
              </SwiperSlide>
            </Swiper>
            {/* <div className="flex overflow-x-auto overflow-y-hidden snap-x snap-mandatory scrollbar-hide">
              <div className="flex-none w-1/2 snap-start px-1">
                <FirstBoxes
                  title="Saldo Keseluruhan"
                  desc={`Rp. ${Number(
                    globalSummary.totalBalance ?? 0,
                  ).toLocaleString("id-ID")},00`}
                />
              </div>
              <div className="flex-none w-1/2 snap-start px-1">
                <FirstBoxes
                  title="Total Penarikan "
                  desc={`Rp. ${Number(
                    globalSummary?.totalWithdrawalAmount ?? 0,
                  ).toLocaleString("id-ID")},00`}
                />
              </div>
              <div className="flex-none w-1/2 snap-start px-1">
                <FirstBoxes
                  title="Total Saldo Masuk"
                  desc={`Rp. ${Number(
                    globalSummary?.totalDepositAmount ?? 0,
                  ).toLocaleString("id-ID")},00`}
                />
              </div>
              <div className="flex-none w-1/2 snap-start px-1">
                <FirstBoxes title="Total Tabungan" desc={`${totalTabungan}`} />
              </div>
            </div> */}
          </section>

          {/* card balance section */}
          <section className="-mt-8">
            <Swiper
              modules={[Pagination]}
              spaceBetween={10}
              slidesPerView={1.12} // Agar card berikutnya terlihat sedikit
              centeredSlides={true}
              pagination={{
                clickable: true,
                el: ".custom-pagination-container", // Menunjuk ke div di luar swiper
                bulletClass:
                  "swiper-pagination-bullet !mx-1 transition-all duration-300",
                bulletActiveClass:
                  "swiper-pagination-bullet-active !w-8 !rounded-full !bg-blue-600",
              }}
              className="pb-8" // Memberi ruang sedikit di bawah slide
            >
              {accounts.map((acc: SavingType, index: number) => {
                const isActive = acc.configId === activeSaving?.configId;
                // return (
                //   <button
                //     key={acc.configId}
                //     onClick={() => {
                //       setActiveSaving({
                //         coaCode: acc.coaCode,
                //         configId: acc.configId,
                //       });
                //       setLastSaving((prev) => ({
                //         ...prev,
                //         [activeCallerId!]: {
                //           coaCode: acc.coaCode,
                //           configId: acc.configId,
                //         },
                //       }));
                //     }}
                //     className={`min-w-[160px] py-2 px-2 rounded-3xl text-xs transition font-montserrat
                //   ${
                //     isActive
                //       ? "bg-psp-btn-primary text-white font-bold"
                //       : "border border-black text-gray-500"
                //   }`}
                //   >
                //     {acc.savingName}
                //   </button>
                // );
                return (
                  <SwiperSlide key={index}>
                    <BalanceCard
                      data={acc}
                      isActive={isActive}
                      activeUser={callerIdentities.filter(
                        (i) => i.callerId === activeCallerId,
                      )}
                      onClick={() => {
                        setActiveSaving({
                          coaCode: acc.coaCode,
                          configId: acc.configId,
                        });
                        setLastSaving((prev) => ({
                          ...prev,
                          [activeCallerId!]: {
                            coaCode: acc.coaCode,
                            configId: acc.configId,
                          },
                        }));
                      }}
                    />
                  </SwiperSlide>
                );
              })}
            </Swiper>
            <div className="custom-pagination-container flex justify-center mt-2 h-2" />
          </section>

          {/* card detail section */}
          <section className="">
            <Swiper
              spaceBetween={5}
              slidesPerView={2.1}
              loop={false}
              keyboard={{
                enabled: true,
              }}
              navigation={true}
              modules={[Autoplay, Pagination, Navigation, Keyboard]}
              className="w-full mb-10 sm:mb-20 p-2"
            >
              <SwiperSlide>
                <FirstBoxes
                  title="Total Saldo"
                  desc={`Rp. ${Number(
                    activeAccount?.balance ?? 0,
                  ).toLocaleString("id-ID")},00`}
                />
              </SwiperSlide>
              <SwiperSlide>
                <FirstBoxes
                  title="Total Transaksi"
                  desc={`${totalTransaksi}`}
                />
              </SwiperSlide>
              <SwiperSlide>
                <FirstBoxes
                  title="Total Penarikan"
                  desc={`Rp. ${Number(
                    summaryByCoa?.totalWithdrawalAmount ?? 0,
                  ).toLocaleString("id-ID")},00`}
                />
              </SwiperSlide>
              <SwiperSlide>
                <FirstBoxes
                  title="Total Saldo Masuk"
                  desc={`Rp. ${Number(
                    summaryByCoa?.totalDepositAmount ?? 0,
                  ).toLocaleString("id-ID")},00`}
                />
              </SwiperSlide>
            </Swiper>
          </section>
        </div>
        <div className="mt-2 h-screen">
          <section className="mb-4 w-full">
            <div className="flex flex-col gap-3">
              {mutationConfig && mutationConfig.length > 0 ? (
                mutationConfig.map((item: mappedType) => {
                  const Mutation = mutationType[item.status];
                  return (
                    <button
                      key={item.id}
                      className="flex items-center justify-between h-full py-3 bg-white rounded-full px-4"
                      onClick={() =>
                        navigate(
                          `/saving/mutation/${item.id}?callerId=${activeCallerId}&coa=${activeSaving?.coaCode}&configId=${activeSaving?.configId}`,
                        )
                      }
                    >
                      <div className="flex items-center flex-1 min-w-0">
                        <div
                          className={`w-12 h-12 flex items-center justify-center rounded-full ${Mutation.icon}`}
                        >
                          <SavingIcon className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex flex-col pl-2 text-left min-w-0">
                          <span className="text-gray-500 text-xs truncate">
                            {Mutation.title}
                          </span>
                          <span className="text-xs">{item.date}</span>
                        </div>
                      </div>
                      <div className="flex gap-1 flex-col items-end justify-center">
                        <button
                          className={`w-6 h-6 rounded-full flex items-center justify-center ${Mutation.icon}`}
                          onClick={() =>
                            navigate(
                              `/saving/mutation/${item.id}?callerId=${activeCallerId}&coa=${activeSaving?.coaCode}&configId=${activeSaving?.configId}`,
                            )
                          }
                        >
                          <ChevronRight className="w-4 h-4 text-white" />
                        </button>
                        <span className="font-semibold text-sm pl-2">
                          {Mutation.sign}
                          {`Rp. 
                          ${Number(item.amount ?? 0).toLocaleString("id-ID")},00`}
                        </span>
                      </div>
                    </button>
                  );
                })
              ) : (
                <>
                  <span className="text-center">
                    -- {t("TRANSACTION.empety")} --
                  </span>
                </>
              )}
            </div>
            {mutationConfig && mutationConfig.length < totalElements && (
              <div className="w-full text-center py-4">
                <span
                  className="font-medium text-sm underline decoration-2 text-blue-700"
                  onClick={() => addMoreMutationSaving()}
                >
                  {t("OTHER.showMore")}
                </span>
              </div>
            )}
          </section>
        </div>
      </div>
      <section>
        {isAccOpen && (
          <div className="fixed inset-0 z-50 flex items-end justify-center">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setIsAccOpen(false)}
            ></div>
            <div className="relative w-full bg-white rounded-t-2xl p-4 animate-slideUp">
              <span className="mb-2 ml-4 mt-3 font-bold">Ganti Akun</span>
              {callerIdentities.map((item) => {
                const isSelected = item.callerId === activeCallerId;
                return (
                  <div
                    key={item.callerId}
                    className="flex items-center justify-between h-20 px-4"
                    onClick={() => {
                      resetSaving();
                      setActiveCallerId(item.callerId);
                      setIsAccOpen(false);
                      setActiveuser(item);
                    }}
                  >
                    <img
                      src={
                        item.photoUrl ||
                        "https://i.pinimg.com/736x/9e/83/75/9e837528f01cf3f42119c5aeeed1b336.jpg"
                      }
                      alt=""
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div className="flex-1 ml-4">
                      <div className="flex flex-col leading-tight">
                        <span>{item.name}</span>
                        <span className="text-gray-500 text-sm">
                          {item.callerId}
                        </span>
                      </div>
                    </div>
                    {isSelected ? (
                      <div className="w-6 h-6 bg-[#0054E6] rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    ) : (
                      <ChevronRight />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default SavingMobile;
